import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  FlightTakeoff as FlightIcon,
  Assignment as AssignmentIcon,
  LocalShipping as ShippingIcon,
  AccountBalanceWallet as WalletIcon,
  GavelOutlined as CustomsIcon,
  TrackChanges as TrackingIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as ProfileIcon,
  Shield as ShieldIcon,
  SupportAgent as SupportIcon,
  Receipt as InvoiceIcon,
  ContactMail as AddressBookIcon,
  BookmarkBorder as TemplateIcon,
  BarChart as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreHoriz as MoreIcon,
  ManageSearch as HsSearchIcon,
  PostAdd as NewDeclarationIcon,
  ListAlt as DeclarationsListIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';

interface SidebarProps {
  width: number;
  mobileOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: string;
  adminOnly?: boolean;
}

const coreItems: NavItem[] = [
  { label: 'Dashboard',          path: '/dashboard',  icon: <DashboardIcon /> },
  { label: 'Book Shipment',      path: '/book',        icon: <span style={{ fontSize: 18 }}>✈️</span>, badge: 'New' },
  { label: 'My Shipments',       path: '/shipments',   icon: <ShippingIcon sx={{ color: '#3b82f6' }} /> },
  { label: 'Last Mile Deliveries', path: '/last-mile', icon: <ShippingIcon sx={{ color: '#f59e0b' }} /> },
  { label: 'HS Code Finder',       path: '/hs-finder', icon: <HsSearchIcon /> },
];

const customsItems: NavItem[] = [
  { label: 'Overview',        path: '/customs',         icon: <CustomsIcon sx={{ color: '#10b981' }} /> },
  { label: 'My Declarations', path: '/customs/cds',     icon: <DeclarationsListIcon sx={{ color: '#10b981' }} /> },
  { label: 'New Declaration', path: '/customs/declare', icon: <NewDeclarationIcon sx={{ color: '#10b981' }} /> },
];

const moreItems: NavItem[] = [
  { label: 'Wallet',         path: '/wallet',      icon: <WalletIcon /> },
  { label: 'Insurance',      path: '/insurance',   icon: <ShieldIcon /> },
  { label: 'Invoices',       path: '/invoices',    icon: <InvoiceIcon /> },
  { label: 'Address Book',   path: '/address-book', icon: <AddressBookIcon /> },
  { label: 'Templates',      path: '/templates',   icon: <TemplateIcon /> },
  { label: 'My Spend & CO₂', path: '/analytics',  icon: <AnalyticsIcon /> },
  { label: 'Support',        path: '/support',     icon: <SupportIcon /> },
  { label: 'Profile & KYC',  path: '/profile',     icon: <ProfileIcon /> },
];

export function Sidebar({ width, mobileOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const isOnCustomsPage = customsItems.some(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
  );
  const [customsOpen, setCustomsOpen] = useState(isOnCustomsPage);

  const isOnMorePage = moreItems.some(
    item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'),
  );
  const [moreOpen, setMoreOpen] = useState(isOnMorePage);

  const handleNavClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #42A5F5 0%, #0066CC 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FlightIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} color="white" lineHeight={1}>
            CAIRgo
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Air Cargo Platform
          </Typography>
        </Box>
      </Box>

      {/* Nav Items */}
      <Box sx={{ flexGrow: 1, py: 2, overflowY: 'auto' }}>
        <List disablePadding>
          {/* Core items */}
          {coreItems.filter(item => !item.adminOnly || user?.role === 'ADMIN' || user?.role === 'CUSTOMS_AGENT').map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <ListItem key={item.path} disablePadding sx={{ px: 1.5, mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavClick(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.25,
                    px: 1.5,
                    color: isActive ? 'white' : 'rgba(255,255,255,0.82)',
                    bgcolor: isActive ? 'rgba(0,185,172,0.18)' : 'transparent',
                    border: isActive ? '1px solid rgba(0,185,172,0.4)' : '1px solid transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: isActive ? '#00C9C4' : 'rgba(148,200,210,0.85)' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 400 }}
                  />
                  {item.badge && (
                    <Chip label={item.badge} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#0066CC', color: 'white' }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}

          {/* Customs & Declarations toggle */}
          <ListItem disablePadding sx={{ px: 1.5, mb: 0.5, mt: 0.5 }}>
            <ListItemButton
              onClick={() => setCustomsOpen(o => !o)}
              sx={{
                borderRadius: 2,
                py: 1.25,
                px: 1.5,
                color: isOnCustomsPage ? 'white' : 'rgba(255,255,255,0.82)',
                bgcolor: isOnCustomsPage ? 'rgba(0,185,172,0.18)' : 'transparent',
                border: isOnCustomsPage ? '1px solid rgba(0,185,172,0.4)' : '1px solid transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                transition: 'all 0.15s ease',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: isOnCustomsPage ? '#00C9C4' : 'rgba(148,200,210,0.85)' }}>
                <CustomsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Customs"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isOnCustomsPage ? 600 : 400 }}
              />
              {customsOpen ? <ExpandLessIcon sx={{ fontSize: 18, opacity: 0.6 }} /> : <ExpandMoreIcon sx={{ fontSize: 18, opacity: 0.6 }} />}
            </ListItemButton>
          </ListItem>

          <Collapse in={customsOpen} timeout="auto" unmountOnExit>
            {customsItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <ListItem key={item.path} disablePadding sx={{ px: 1.5, mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavClick(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      pl: 3,
                      pr: 1.5,
                      color: isActive ? 'white' : 'rgba(255,255,255,0.78)',
                      bgcolor: isActive ? 'rgba(0,185,172,0.18)' : 'transparent',
                      border: isActive ? '1px solid rgba(0,185,172,0.4)' : '1px solid transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: isActive ? '#00C9C4' : 'rgba(148,200,210,0.85)' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: isActive ? 600 : 400 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Collapse>

          {/* More toggle */}
          <ListItem disablePadding sx={{ px: 1.5, mb: 0.5, mt: 0.5 }}>
            <ListItemButton
              onClick={() => setMoreOpen(o => !o)}
              sx={{
                borderRadius: 2,
                py: 1,
                px: 1.5,
                color: isOnMorePage ? 'white' : 'rgba(255,255,255,0.82)',
                bgcolor: isOnMorePage ? 'rgba(255,255,255,0.07)' : 'transparent',
                border: '1px solid transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                transition: 'all 0.15s ease',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                <MoreIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="More"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 400 }}
              />
              {moreOpen ? <ExpandLessIcon sx={{ fontSize: 18, opacity: 0.6 }} /> : <ExpandMoreIcon sx={{ fontSize: 18, opacity: 0.6 }} />}
            </ListItemButton>
          </ListItem>

          {/* Collapsible more items */}
          <Collapse in={moreOpen} timeout="auto" unmountOnExit>
            {moreItems.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <ListItem key={item.path} disablePadding sx={{ px: 1.5, mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavClick(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      pl: 3,
                      pr: 1.5,
                      color: isActive ? 'white' : 'rgba(255,255,255,0.78)',
                      bgcolor: isActive ? 'rgba(0,185,172,0.18)' : 'transparent',
                      border: isActive ? '1px solid rgba(0,185,172,0.4)' : '1px solid transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' },
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: isActive ? '#00C9C4' : 'rgba(148,200,210,0.85)' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: isActive ? 600 : 400 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Collapse>
        </List>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Settings */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <ListItemButton
          onClick={() => handleNavClick('/settings')}
          sx={{
            borderRadius: 2,
            py: 1.25,
            px: 1.5,
            color: 'rgba(255,255,255,0.6)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'white' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </ListItemButton>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* User profile */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.05)',
            mb: 1,
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#0066CC',
              fontSize: '0.875rem',
              fontWeight: 700,
            }}
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="white"
              noWrap
            >
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.5)' }}
              noWrap
            >
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            py: 1,
            px: 1.5,
            color: 'rgba(255,255,255,0.5)',
            '&:hover': {
              bgcolor: 'rgba(239,68,68,0.1)',
              color: '#f87171',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{ fontSize: '0.875rem' }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  const drawerSx = {
    '& .MuiDrawer-paper': {
      width,
      boxSizing: 'border-box',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      border: 'none',
    },
  };

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          ...drawerSx,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', lg: 'block' },
          ...drawerSx,
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
